import { useRouterState } from '@tanstack/react-router';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

export type TelemetryBarStatus = 'closed' | 'overlay';

interface TelemetryBarContextType {
  telemetryBarStatus: TelemetryBarStatus;
  telemetryHasAnimated: boolean;
  setTelemetryHasAnimated: React.Dispatch<React.SetStateAction<boolean>>;
  closeTelemetryBar: () => void;
  toggleTelemetryVisibility: () => void;
}

const TelemetryBarContext = createContext<TelemetryBarContextType | null>(null);

export function TelemetryBarProvider({ children }: { children: React.ReactNode }) {
  const [telemetryBarStatus, setTelemetryBarStatus] = useState<TelemetryBarStatus>('closed');

  // Track if the telemetry overlay has animated in to prevent re-animating on page navigation.
  const [telemetryHasAnimated, setTelemetryHasAnimated] = useState(false);

  const closeTelemetryBar = useCallback(() => {
    setTelemetryBarStatus('closed');
  }, []);

  const toggleTelemetryVisibility = useCallback(() => {
    setTelemetryBarStatus((prev) => (prev === 'overlay' ? 'closed' : 'overlay'));
  }, []);

  // Close the telemetry overlay whenever the route changes.
  const pathname = useRouterState({
    select: (s) => s.location.pathname,
  });
  useEffect(() => {
    setTelemetryBarStatus('closed');
  }, [pathname]);

  const value: TelemetryBarContextType = {
    closeTelemetryBar,
    setTelemetryHasAnimated,
    telemetryBarStatus,
    telemetryHasAnimated,
    toggleTelemetryVisibility,
  };

  return <TelemetryBarContext.Provider value={value}>{children}</TelemetryBarContext.Provider>;
}

export function useTelemetryBar() {
  const context = useContext(TelemetryBarContext);
  if (!context) {
    throw new Error('useTelemetryBar must be used within a TelemetryBarProvider');
  }
  return context;
}
